import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { ROLE_LABELS } from '@/lib/roles';
import { toolsAPI, rechtenAPI, usersAPI, activityAPI } from '@/lib/api';
import AppLayout from '@/components/AppLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ExternalLink, Globe, Shield, Users as UsersIcon, Wrench, Activity,
  ShieldCheck, MapPin, ChevronRight,
} from 'lucide-react';

// ----- Helpers -----------------------------------------------------------

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'zojuist';
  if (diffSec < 3600) {
    const m = Math.floor(diffSec / 60);
    return `${m} min geleden`;
  }
  if (diffSec < 86400) {
    const h = Math.floor(diffSec / 3600);
    return `${h} ${h === 1 ? 'uur' : 'uur'} geleden`;
  }
  const days = Math.floor(diffSec / 86400);
  if (days < 7) return `${days} ${days === 1 ? 'dag' : 'dagen'} geleden`;
  const dd = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mo}`;
}

const ACTIE_LABELS = {
  login: 'Login',
  logout: 'Uitgelogd',
  'logout-via-redirect': 'Uitgelogd (cross-tool)',
  'create-user': 'Gebruiker aangemaakt',
  'update-user': 'Gebruiker bijgewerkt',
  'delete-user': 'Gebruiker verwijderd',
  'update-rechten': 'Rechten bijgewerkt',
  'create-tool': 'Tool aangemaakt',
  'update-tool': 'Tool bijgewerkt',
  'delete-tool': 'Tool verwijderd',
  'regenerate-key': 'API-sleutel vernieuwd',
  'sso-blocked-no-access': 'SSO geblokkeerd',
  'terminate-session': 'Sessie beëindigd',
  'reset-password': 'Wachtwoord reset',
  'create-location': 'Locatie aangemaakt',
  'update-location': 'Locatie bijgewerkt',
  'delete-location': 'Locatie verwijderd',
  'update-tool-rollen': 'Tool-rollen bijgewerkt',
};

function actieLabel(actie) {
  return ACTIE_LABELS[actie] || actie;
}

function actieTone(actie) {
  if (actie === 'sso-blocked-no-access') return 'danger';
  if (actie?.startsWith('delete')) return 'danger';
  if (actie?.startsWith('create')) return 'success';
  if (actie === 'login' || actie === 'logout' || actie === 'logout-via-redirect') return 'info';
  return 'neutral';
}

const TONE_STYLES = {
  danger: 'bg-red-50 text-red-700 border-red-100',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  info: 'bg-sky-50 text-sky-700 border-sky-100',
  neutral: 'bg-slate-50 text-slate-600 border-slate-200',
};

// ----- Page -------------------------------------------------------------

export default function DashboardPage() {
  const { user, hasMinRole } = useAuth();
  const [tools, setTools] = useState([]);
  const [userRechten, setUserRechten] = useState({});
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ users: null, tools: null, activity24h: null });
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);

  const isSimpleUser = !hasMinRole('admin');
  const canSeeUsers = hasMinRole('admin');
  const canSeeActivity = hasMinRole('admin');

  const today = new Date().toLocaleDateString('nl-NL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const loadCore = useCallback(async () => {
    try {
      const toolsRes = await toolsAPI.list();
      if (toolsRes.ok) {
        const toolsData = await toolsRes.clone().json();
        setTools(toolsData);

        const rechtenMap = {};
        for (const tool of toolsData) {
          try {
            const res = await rechtenAPI.getMine(tool.tool_id);
            if (res.ok) {
              rechtenMap[tool.tool_id] = await res.clone().json();
            }
          } catch { /* tolerate per-tool failure */ }
        }
        setUserRechten(rechtenMap);
      }
    } catch { /* tolerate */ } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    if (isSimpleUser) return;
    const next = { users: null, tools: null, activity24h: null };
    try {
      if (canSeeUsers) {
        const r = await usersAPI.list();
        if (r.ok) next.users = (await r.clone().json()).length;
      }
    } catch { /* tolerate */ }
    try {
      const r = await toolsAPI.list();
      if (r.ok) {
        const arr = await r.clone().json();
        next.tools = arr.filter(t => t.status === 'actief').length;
      }
    } catch { /* tolerate */ }
    try {
      if (canSeeActivity) {
        const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
        const r = await activityAPI.list({ from_date: since, limit: 1 });
        if (r.ok) next.activity24h = (await r.clone().json()).total || 0;
      }
    } catch { /* tolerate */ }
    setStats(next);
  }, [isSimpleUser, canSeeUsers, canSeeActivity]);

  const loadRecent = useCallback(async () => {
    if (!canSeeActivity) { setRecentLoading(false); return; }
    try {
      const r = await activityAPI.list({ limit: 8 });
      if (r.ok) {
        const data = await r.clone().json();
        setRecent(data.items || []);
      }
    } catch { /* tolerate */ } finally {
      setRecentLoading(false);
    }
  }, [canSeeActivity]);

  useEffect(() => {
    loadCore();
    loadStats();
    loadRecent();
  }, [loadCore, loadStats, loadRecent]);

  const accessibleTools = tools.filter(t => userRechten[t.tool_id]?.toegang);

  // Auto-redirect for simple users with only 1 tool
  useEffect(() => {
    if (!loading && isSimpleUser && accessibleTools.length === 1) {
      const tool = accessibleTools[0];
      if (tool.url) window.location.href = tool.url;
    }
  }, [loading, isSimpleUser, accessibleTools]);

  if (isSimpleUser) {
    return (
      <SimpleUserDashboard
        user={user} today={today} loading={loading}
        accessibleTools={accessibleTools}
      />
    );
  }

  return (
    <AppLayout>
      {/* Welcome banner — compact */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3" data-testid="dashboard-welcome-banner">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1a2332] tracking-tight">
            Welkom terug, {user?.naam}
          </h1>
          <p className="text-sm text-slate-500 mt-1 tabular-nums">{today}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50">
            {ROLE_LABELS[user?.globale_rol] || user?.globale_rol}
          </Badge>
          {user?.locations?.length > 0 && (
            <Badge variant="outline" className="text-slate-600">
              {user.locations.join(', ')}
            </Badge>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8" data-testid="dashboard-stats-row">
        <StatCard
          icon={UsersIcon}
          label="Gebruikers"
          value={stats.users}
          hint="Totaal aangemaakt"
          tone="navy"
          testId="stat-users"
        />
        <StatCard
          icon={Wrench}
          label="Actieve tools"
          value={stats.tools}
          hint={tools.length ? `${tools.length} totaal geregistreerd` : ''}
          tone="gold"
          testId="stat-tools"
        />
        {canSeeActivity && (
          <StatCard
            icon={Activity}
            label="Activiteit (24u)"
            value={stats.activity24h}
            hint="Aantal events laatste 24 uur"
            tone="emerald"
            testId="stat-activity"
          />
        )}
      </div>

      {/* Main grid: tools + activity sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tools column */}
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#1a2332]">Jouw tools</h2>
              {accessibleTools.length > 0 && (
                <span className="text-xs text-slate-500">{accessibleTools.length} beschikbaar</span>
              )}
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="border border-slate-200">
                    <CardHeader>
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-4 w-48 mt-2" />
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-8 w-24" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : accessibleTools.length === 0 ? (
              <Card className="border border-slate-200">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500">Je hebt nog geen toegang tot tools.</p>
                  <p className="text-xs text-slate-400 mt-1">Neem contact op met een beheerder.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {accessibleTools.map(tool => (
                  <ToolCard
                    key={tool.tool_id}
                    tool={tool}
                    rechten={userRechten[tool.tool_id]}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Quick actions */}
          <QuickActions canSeeUsers={canSeeUsers} hasMinRole={hasMinRole} />
        </div>

        {/* Activity sidebar */}
        {canSeeActivity && (
          <div className="lg:col-span-1">
            <RecentActivityCard items={recent} loading={recentLoading} />
          </div>
        )}
      </div>
    </AppLayout>
  );
}

// ----- Sub-components ---------------------------------------------------

function StatCard({ icon: Icon, label, value, hint, tone, testId }) {
  const toneStyles = {
    navy:    { ring: 'bg-[#1a2332]', icon: 'text-[#d4a853]' },
    gold:    { ring: 'bg-[#d4a853]', icon: 'text-[#1a2332]' },
    emerald: { ring: 'bg-emerald-600', icon: 'text-white' },
  };
  const t = toneStyles[tone] || toneStyles.navy;
  return (
    <Card className="border border-slate-200" data-testid={testId}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-xl ${t.ring} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${t.icon}`} />
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</p>
          <p className="text-2xl font-semibold text-[#1a2332] tabular-nums leading-tight">
            {value === null || value === undefined ? '—' : value}
          </p>
          {hint && <p className="text-xs text-slate-400 mt-0.5 truncate">{hint}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function ToolCard({ tool, rechten }) {
  const accessiblePages = rechten?.paginas
    ? Object.entries(rechten.paginas).filter(([, v]) => v).map(([k]) => k)
    : [];
  const displayPages = accessiblePages.slice(0, 3);
  const moreCount = accessiblePages.length - 3;

  return (
    <Card className="border border-slate-200 tool-card" data-testid="dashboard-tool-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-[#1a2332] flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-[#d4a853]" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base truncate">{tool.display_name}</CardTitle>
              <CardDescription className="text-xs mt-0.5 truncate">{tool.beschrijving}</CardDescription>
            </div>
          </div>
          <Badge className={tool.status === 'actief'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50 flex-shrink-0'
            : 'bg-red-50 text-red-700 border-red-100 hover:bg-red-50 flex-shrink-0'
          }>
            {tool.status === 'actief' ? 'Actief' : 'Inactief'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {displayPages.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {displayPages.map(p => (
              <span key={p} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                {p}
              </span>
            ))}
            {moreCount > 0 && (
              <span className="text-xs text-slate-400">+{moreCount} meer</span>
            )}
          </div>
        )}
        <a href={tool.url} target="_blank" rel="noopener noreferrer">
          <Button
            className="w-full bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl btn-press transition-colors duration-150"
            data-testid="dashboard-tool-open-button"
          >
            Open tool <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </a>
      </CardContent>
    </Card>
  );
}

function QuickActions({ canSeeUsers, hasMinRole }) {
  const navigate = useNavigate();
  const actions = [
    { label: 'Gebruikers',     tab: 'gebruikers',      icon: UsersIcon,    minRole: 'admin' },
    { label: 'Rechten',        tab: 'rechten',         icon: ShieldCheck,  minRole: 'owner' },
    { label: 'Activiteitenlog', tab: 'activiteitenlog', icon: Activity,    minRole: 'admin' },
    { label: 'Tools',          tab: 'tools',           icon: Wrench,       minRole: 'super_owner' },
    { label: 'Locaties',       tab: 'locaties',        icon: MapPin,       minRole: 'super_owner' },
  ];
  const visible = actions.filter(a => hasMinRole(a.minRole));
  if (!canSeeUsers || visible.length === 0) return null;

  return (
    <div data-testid="dashboard-quick-actions">
      <h2 className="text-lg font-semibold text-[#1a2332] mb-3">Snelle acties</h2>
      <div className="flex flex-wrap gap-2">
        {visible.map(a => (
          <button
            key={a.tab}
            onClick={() => navigate(`/instellingen?tab=${a.tab}`)}
            className="group flex items-center gap-2 bg-white border border-slate-200 hover:border-[#d4a853]/50 hover:shadow-sm rounded-xl px-3.5 py-2 text-sm text-[#1a2332] font-medium transition-all duration-150"
            data-testid={`dashboard-quick-action-${a.tab}`}
          >
            <a.icon className="w-4 h-4 text-slate-500 group-hover:text-[#d4a853] transition-colors" />
            {a.label}
            <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#d4a853] transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
}

function RecentActivityCard({ items, loading }) {
  const navigate = useNavigate();
  return (
    <Card className="border border-slate-200" data-testid="dashboard-recent-activity">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Recente activiteit</CardTitle>
          <CardDescription className="text-xs mt-0.5">Laatste 8 events</CardDescription>
        </div>
        <button
          onClick={() => navigate('/instellingen?tab=activiteitenlog')}
          className="text-xs text-[#d4a853] hover:text-[#c79b45] font-medium transition-colors duration-150"
          data-testid="dashboard-activity-see-all"
        >
          Alles →
        </button>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="w-6 h-6 text-slate-300 mx-auto mb-2" />
            <p className="text-xs text-slate-400">Geen recente activiteit</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="dashboard-activity-list">
            {items.map(item => {
              const tone = actieTone(item.actie);
              return (
                <li key={item.id} className="py-2.5 first:pt-0 last:pb-0" data-testid="dashboard-activity-item">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${TONE_STYLES[tone]} font-medium`}>
                          {actieLabel(item.actie)}
                        </span>
                        {item.tool_id && item.tool_id !== 'identity' && (
                          <span className="text-[11px] text-slate-400">{item.tool_id}</span>
                        )}
                      </div>
                      <p className="text-sm text-[#1a2332] mt-1 truncate" title={item.details}>
                        {item.user_naam}
                      </p>
                      {item.details && (
                        <p className="text-xs text-slate-500 truncate" title={item.details}>
                          {item.details}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-slate-400 whitespace-nowrap tabular-nums flex-shrink-0">
                      {relativeTime(item.timestamp)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ----- Simple dashboard for locatie / kluisbeheer ----------------------

function SimpleUserDashboard({ user, today, loading, accessibleTools }) {
  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Minimal header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#1a2332] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#d4a853]" />
            </div>
            <span className="font-bold text-[#1a2332]">TW1</span>
          </div>
          <SimpleUserMenu user={user} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="simple-user-dashboard">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1a2332] tracking-tight">
            Hallo, {user?.naam}
          </h1>
          <p className="text-slate-500 mt-1 tabular-nums">{today}</p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map(i => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
          </div>
        ) : accessibleTools.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500">Je hebt nog geen toegang tot tools.</p>
              <p className="text-xs text-slate-400 mt-1">Neem contact op met een beheerder.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {accessibleTools.map(tool => (
              <a
                key={tool.tool_id}
                href={tool.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                data-testid="simple-user-tool-card"
              >
                <Card className="border border-slate-200 hover:border-[#d4a853]/40 hover:shadow-md transition-all duration-200 cursor-pointer">
                  <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#1a2332] flex items-center justify-center flex-shrink-0">
                        <Globe className="w-6 h-6 text-[#d4a853]" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1a2332]">{tool.display_name}</p>
                        {tool.beschrijving && (
                          <p className="text-sm text-slate-500 mt-0.5">{tool.beschrijving}</p>
                        )}
                      </div>
                    </div>
                    <ExternalLink className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function SimpleUserMenu({ user }) {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const initials = user?.naam?.charAt(0)?.toUpperCase() || '?';

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 transition-colors duration-150"
        data-testid="simple-user-profile-button"
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white"
          style={{ backgroundColor: user?.avatar_kleur || '#1a2332' }}
        >
          {initials}
        </div>
        <span className="text-sm font-medium text-[#1a2332] hidden sm:block">{user?.naam}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1">
            <div className="px-3 py-2 border-b border-slate-100">
              <p className="text-sm font-medium text-[#1a2332]">{user?.naam}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
            <button
              onClick={() => { logout(); }}
              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
              data-testid="simple-user-logout-button"
            >
              Uitloggen
            </button>
          </div>
        </>
      )}
    </div>
  );
}
